import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { DashboardResolve, ContractRequest, ProviderLinkOptions } from '@shared/models';
import { ActivatedRoute } from '@angular/router';
import { formatAddress, getContractPriceAdjustmentIncreaseString, TypedRoute } from '@shared/utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeatureFlagService, ProviderSettingsService } from '@core/services';
import { LocalizedCurrencyPipe, LocalizedPercentagePipe } from '@core/pipes';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-dashboard-contract-requests',
  templateUrl: './dashboard-contract-requests.component.html',
  styleUrl: './dashboard-contract-requests.component.scss',
})
export class DashboardContractRequestsComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  contractRequests: ContractRequest[] = [];
  providerLinks!: ProviderLinkOptions;

  constructor(
    @Inject(ActivatedRoute) private route: TypedRoute<DashboardResolve>,
    protected featureFlagService: FeatureFlagService,
    private localizedCurrencyPipe: LocalizedCurrencyPipe,
    private localizedPercentagePipe: LocalizedPercentagePipe,
    private translateService: TranslateService,
    private providerSettingsService: ProviderSettingsService,
  ) {}

  ngOnInit(): void {
    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.contractRequests = this.sortContracts(data.dashboardApiResponse.contract_requests);
    });

    this.providerSettingsService.providerSettings.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.providerLinks = this.providerSettingsService.getLinks();
    });
  }

  sortContracts(contractRequests: ContractRequest[]): ContractRequest[] {
    const statusOrder: Partial<Record<ContractRequest['status'], number>> = {
      pending: 0,
      waiting_list: 1,
      declined: 2,
    };

    return [...contractRequests].sort((a, b) => (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99));
  }

  hasCompleteGarageAddress(contractRequest: ContractRequest): boolean {
    if (!contractRequest.garage) {
      return false;
    }
    return (
      !!contractRequest.garage.name && !!contractRequest.garage.city && !!contractRequest.garage.street && !!contractRequest.garage.number && !!contractRequest.garage.zip_code
    );
  }

  getPriceIncreaseString(contractRequest: ContractRequest): string | null {
    return getContractPriceAdjustmentIncreaseString(
      contractRequest,
      'Dashboard.Contracts.PriceIncreaseInfo',
      this.translateService,
      this.localizedCurrencyPipe,
      this.localizedPercentagePipe,
    );
  }

  get showVpi() {
    return this.featureFlagService.getFeatureFlag('enable_wipark_vpi');
  }



  protected readonly formatAddress = formatAddress;
}
